/**
 * Status, Board-Gruppierung, Dokumentenstand und Handlungsbedarf - 1:1-Portierung aus `index.html`
 * (Stand 2026-08-08). Die Rechenwege sind woertlich uebernommen, auch wo sie umstaendlich wirken
 * oder auf den ersten Blick fehlerhaft aussehen; ergaenzt wurden ausschliesslich Typen. Namen und
 * Logik sind unangetastet.
 *
 * ================= Der eine bewusste Umbau: setStatusDefinitions =================
 *
 * Im Original schreibt `setStatusDefinitions` die Modul-Globals `STATUSES`, `STATUS_MAP` und
 * `STATUS_STAGE` (index.html:4137-4139, 4145-4160); alle davon abhaengigen Funktionen lesen sie
 * von dort. Hier ist es eine reine Funktion, die diese drei als Objekt (`StatusDefinitionen`)
 * zurueckgibt; die abhaengigen Funktionen bekommen das Objekt als zusaetzliches Argument. Das ist
 * der einzige Eingriff in die Aufrufform - die Rechenwege darin sind unveraendert.
 *
 * NICHT uebernommen wurde die Schleife index.html:4156-4159, die `COLUMN_FILTERS` und
 * `COLUMN_FILTER_OPEN` mit einem Eintrag je Statuskennung vorbelegt. Beide sind
 * Oberflaechenzustand: `COLUMN_FILTERS[id]` haelt den Text der Spaltenfilter-Eingabefelder
 * (`orderNo`, `from`, `to`), `COLUMN_FILTER_OPEN[id]` merkt sich, ob das Filterfeld einer Spalte
 * aufgeklappt ist (index.html:6380-6404). Sie werden ausschliesslich von der Board-Ansicht gelesen
 * und geschrieben, gehen in keine Berechnung ein und wirken nicht auf STATUSES/STATUS_MAP/
 * STATUS_STAGE zurueck. In dieser Phase gehoert Oberflaechenzustand nicht nach `lib/`; die
 * Vorbelegung ist beim Bau der Board-Ansicht aus `STATUSES` abzuleiten. Der Differenztest haelt
 * das Verhalten des Originals an dieser Stelle ausdruecklich fest.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `STATUS_ICONS[column.icon]` (index.html:4149-4150) ist ein ungesicherter Zugriff auf ein
 *    Objekt mit `Object.prototype` in der Kette. Eine Spalte mit `icon: "constructor"` greift
 *    durch, gilt als "bekanntes Icon" und landet samt `iconSvg: Object` im Ergebnis. Das ist hier
 *    ABSICHTLICH nicht per `Object.hasOwn` abgesichert: das waere eine Verhaltensaenderung in einer
 *    Phase, die woertliche Uebernahme verlangt. Die Typeinengung, die der geschlossene Union-Typ
 *    `StatusIconKey` erzwingt, ist ueber einen Cast geloest, der zur Laufzeit exakt denselben
 *    Zugriff ausfuehrt (siehe `ikonenTabelle` unten).
 * 2. Dieselbe Form haben `STATUS_MAP[order?.status]` (index.html:6613) und
 *    `STATUS_STAGE[status]` (index.html:4164, 13696). Auch sie bleiben ungesichert. Dort tritt
 *    kein Wert nach aussen: die Ergebnisse sind Wahrheitswerte bzw. Zahlen, und ein
 *    durchgegriffener Prototyp-Wert faellt ueber `Number(...)` oder `?.label` in denselben
 *    Rueckfall wie ein fehlender Eintrag.
 * 3. `visibleDocTypes` ist im Original ohne Parameter deklariert, wird aber durchgehend mit der
 *    Bestellung aufgerufen (`visibleDocTypes(o)`, index.html:5047, 5050, 12643). Der Parameter war
 *    hier deshalb ein ungenutzter Platzhalter; seit dem Anzahlungsbeleg (Asana 1220057765558651,
 *    Spec 2026-10-31) wird er gelesen - siehe ABWEICHUNG an `visibleDocTypes` und `docProgress`.
 *
 * ================= Notwendige Abhaengigkeiten =================
 *
 * Einige Hilfsfunktionen des Originals stehen nicht auf der Portierungsliste dieses Tasks, werden
 * von ihr aber gebraucht (`docsForType`, `isArrivedStatus`, `isSplitCollectionOrder`). Sie sind hier
 * modulintern und bewusst NICHT exportiert - dasselbe Vorgehen wie bei `orderExcelCell` in
 * `format.ts`. Sobald ein Folgetask sie eigenstaendig portiert, ist diese Fassung durch einen Import
 * zu ersetzen.
 *
 * Vier weitere waren bis Task 5a aus demselben Grund hier modulintern: `isPaid` (index.html:5058),
 * `nextDue` (5143), `isPaymentRequestTask` (5177) und `openPaymentRequestTask` (5223). Ihr
 * fachlicher Ort ist `lib/payments.ts`; sie stehen ab Task 5a dort und werden hier importiert statt
 * ein zweites Mal portiert. Die Importrichtung ist `lib/status.ts` -> `lib/payments.ts`;
 * `lib/payments.ts` kommt ohne dieses Modul aus, es entsteht also kein Zyklus.
 *
 * Ebenso umgezogen ist `normSearch` (6349) - nach `lib/format.ts`, weil `lib/payments.ts` es
 * braucht und ein Export aus diesem Modul in die Gegenrichtung ein Zyklus waere. Begruendung im
 * Einzelnen dort. `lib/status.ts` selbst brauchte es nur ueber `isSplitStatusOrder`; seit deren
 * Entfernen (Statusbereinigung) importiert dieses Modul `normSearch` nicht mehr.
 *
 * ================= Additiv mit Task 6: activeReportingOrders, paymentCalendarRows =================
 *
 * Zwei weitere Funktionen kommen additiv mit Task 6 hinzu, obwohl sie in dessen Planliste unter
 * einem anderen Zielmodul (`src/lib/fx.ts`) stehen: `activeReportingOrders` (index.html:6616) und
 * `paymentCalendarRows` (13354). `fxWeekBuckets` (`fx.ts`) braucht beide; in `lib/payments.ts`
 * ergaebe `paymentCalendarRows` einen Importzyklus (sie braucht `orderListDue` von hier, und dieses
 * Modul importiert bereits `lib/payments.ts`). Deshalb liegen sie hier statt in `fx.ts` - Entscheid
 * des Controllers, Begruendung im Bericht zu Task 6. Neu ist damit auch der Import aus
 * `lib/suppliers.ts` (`orderCurrency`, `Supplier`): `lib/suppliers.ts` importiert seinerseits nichts
 * aus `lib/status.ts`, es entsteht also kein Zyklus.
 */
import {
  BOARD_GROUPS,
  DEFAULT_BOARD_COLUMNS,
  DEFAULT_ORDER_LIST_COLUMNS,
  REMOVED_ORDER_LIST_KEYS,
  STATUS_ICONS,
} from "../config/board.ts";
import { CMR_DOC_TYPE, DOC_TYPES, type DocType } from "../config/documents.ts";
import { dokumenteVollstaendig } from "./dokumentStand.ts";
import { daysFromToday, fmtDate, todayIso } from "./format.ts";
import {
  isPaid,
  nextDue,
  openPaymentRequestTask,
  orderTotal,
  payAmount,
  paymentStatusOf,
} from "./payments.ts";
import { orderCurrency, type Supplier } from "./suppliers.ts";
import { ZAHLUNGSBELEG_TYPEN, anzahlungsbelegPflicht, istBelegFuer, type Zahlungsbelegtyp } from "./zahlungsbelege.ts";
import { stufeFuerStatus } from "./zeitstrahlStufen.ts";

// ================= Typen =================

/**
 * Eine Board-Spalte, wie sie aus gespeicherten Einstellungen kommt: jedes Feld kann fehlen oder
 * einen unerwarteten Typ haben. Der Auslieferungszustand (`BoardColumn` aus `config/board.ts`) ist
 * ein Sonderfall davon.
 */
export interface BoardColumnLike {
  id?: string;
  label?: string;
  color?: string;
  icon?: string;
  position?: number;
  stage?: number;
}

/** Eine Bestelllisten-Spalte aus gespeicherten Einstellungen. */
export interface OrderListColumnLike {
  id?: string;
  key?: string;
  label?: string;
  position?: number;
  visible?: boolean;
  custom?: boolean;
}

/** Eine Board-Spalte nach der Aufbereitung durch `setStatusDefinitions`. */
export interface StatusDefinition extends BoardColumnLike {
  label: string;
  color: string;
  icon: string;
  /** Aufgehellte Fassung von `color` fuer Flaechen (`statusSoftColor`). */
  soft: string;
  /**
   * Lucide-Iconname zu `icon`. Trotz des Namens kein SVG - so heisst das Feld im Original.
   * Bei einem `icon`, das auf `Object.prototype` trifft, steht hier der durchgegriffene Wert
   * (siehe Eigenheit 1 im Kopfkommentar).
   */
  iconSvg: string;
}

/**
 * Die drei Ableitungen, die im Original Modul-Globals sind. Rueckgabe von `setStatusDefinitions`,
 * Argument aller davon abhaengigen Funktionen.
 */
export interface StatusDefinitionen {
  STATUSES: StatusDefinition[];
  STATUS_MAP: Record<string, StatusDefinition>;
  STATUS_STAGE: Record<string, number>;
}

// Die Typdeklarationen dieses Moduls stehen seit Task 1 gebuendelt in `lib/types.ts` (Begruendung
// dort). Re-Export unter den bisherigen Namen, damit kein bestehender Importpfad bricht.
import type { Order, OrderDocument, OrderPayment, OrderTask, OrderItem } from "./types.ts";
export type { Order, OrderDocument, OrderPayment, OrderTask, OrderItem };

/** Ein Handlungsbedarfs-Hinweis aus `actionInfo`/`overviewActionInfo`. */
export interface ActionLabel {
  text?: string;
  /** 2 = dringend (rot), 1 = bald (gelb), 0 = ok. */
  level?: number;
  kind?: string;
}

/** Handlungsbedarf einer Bestellung. */
export interface ActionInfo {
  level: number;
  labels: ActionLabel[];
}

/** Dokumentenstand einer Bestellung. */
export interface DocProgress {
  done: number;
  total: number;
}

// ================= Board-Spalten =================

/**
 * index.html:4075.
 *
 * Traegt bis zur Statusbereinigung zusaetzlich die Spalten "split" und "sammellieferung" nach,
 * falls sie fehlten - beide Bloecke sind mit den gleichnamigen Board-Status-Spalten entfallen
 * (siehe `config/board.ts`, `DEFAULT_BOARD_COLUMNS`). Der verbleibende Nachtrag der Spalte
 * "abgeschlossen" ist davon unabhaengig und bleibt bestehen.
 */
export function ensureCompletedBoardColumn(columns: unknown): BoardColumnLike[] {
  const source: readonly BoardColumnLike[] =
    Array.isArray(columns) && columns.length ? (columns as BoardColumnLike[]) : DEFAULT_BOARD_COLUMNS;
  const normalized = source
    .slice()
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    .map((column, position) => ({ ...column, position }));
  const hasColumn = (id: string, label: string): boolean =>
    normalized.some(
      (column) =>
        column.id === id || String(column.label || "").trim().toLocaleLowerCase("de-DE") === label,
    );
  if (!hasColumn("abgeschlossen", "abgeschlossen") && normalized.length < 20) {
    const completed = DEFAULT_BOARD_COLUMNS.find((column) => column.id === "abgeschlossen");
    normalized.push({ ...completed, position: normalized.length });
  }
  return normalized.map((column, position) => ({ ...column, position }));
}

/**
 * index.html:4124.
 *
 * Eine in der persistierten Liste fehlende Vorgabespalte haengt hier NICHT stumpf ans Ende
 * (`position: normalized.length`), sondern unmittelbar hinter ihren Vorgaenger aus
 * `DEFAULT_ORDER_LIST_COLUMNS`: rueckwaerts ab der fehlenden Spalte gesucht, der erste Schluessel,
 * der in der (ggf. schon einmal ergaenzten) Liste tatsaechlich vorkommt. Die neue Spalte bekommt
 * dessen `position` plus 0.5 - eine Zwischen-Position, die beim abschliessenden Sortieren
 * unmittelbar nach dem Vorgaenger und vor allem Nachfolgenden landet, ohne bestehende
 * Positionswerte anzufassen. Gibt es keinen Vorgaenger (die fehlende Spalte ist selbst die erste
 * Vorgabespalte, oder alle vorherigen Vorgabespalten fehlen ebenfalls), bleibt der alte Rueckfall
 * ans Ende bestehen. `positionByKey` wird nach jeder Einfuegung nachgezogen, damit eine spaeter
 * geprueft fehlende Spalte die gerade erst eingefuegte Position einer frueher eingefuegten als
 * moeglichen Vorgaenger sieht (zwei aufeinanderfolgende fehlende Vorgabespalten landen so
 * hintereinander, nicht beide am Ende).
 */
export function normalizeOrderListColumns(columns: unknown): OrderListColumnLike[] {
  const source: readonly OrderListColumnLike[] =
    Array.isArray(columns) && columns.length
      ? (columns as OrderListColumnLike[])
      : DEFAULT_ORDER_LIST_COLUMNS;
  const normalized = source
    // `column.key` kann fehlen; `Set.has(undefined)` ist zur Laufzeit unproblematisch, nur der Typ
    // verlangt hier eine Einengung. Reine Typergaenzung, kein Eingriff in den Ablauf.
    .filter((column) => column && !REMOVED_ORDER_LIST_KEYS.has(column.key as string))
    .map((column) => ({ ...column }));
  const positionByKey = new Map(normalized.map((column) => [column.key, Number(column.position || 0)]));
  const defaultIndexByKey = new Map(DEFAULT_ORDER_LIST_COLUMNS.map((column, index) => [column.key, index]));
  for (const column of DEFAULT_ORDER_LIST_COLUMNS) {
    if (positionByKey.has(column.key)) continue;
    let vorgaengerPosition: number | undefined;
    for (let index = (defaultIndexByKey.get(column.key) as number) - 1; index >= 0; index -= 1) {
      const vorgaengerKey = DEFAULT_ORDER_LIST_COLUMNS[index].key;
      if (positionByKey.has(vorgaengerKey)) {
        vorgaengerPosition = positionByKey.get(vorgaengerKey);
        break;
      }
    }
    const position = vorgaengerPosition === undefined ? normalized.length : vorgaengerPosition + 0.5;
    normalized.push({ ...column, position });
    positionByKey.set(column.key, position);
  }
  return normalized
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    .map((column, position) => ({ ...column, position }));
}

/** index.html:4140 */
export function statusSoftColor(color: unknown): string {
  // `hex` ist im Original der ungepruefte Eingabewert, sobald er auf das Muster passt (nicht der
  // per String() erzeugte Text) - deshalb hier ein Cast statt einer Umwandlung.
  const hex = /^#[0-9a-f]{6}$/i.test(String(color || "")) ? (color as string) : "#667085";
  const rgb = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgb(${rgb.map((v) => Math.round(v + (255 - v) * 0.86)).join(",")})`;
}

/**
 * Sicht auf `STATUS_ICONS` mit offenem Schluesseltyp. Der Cast ist reine Typarbeit: er erlaubt die
 * Indizierung mit einem beliebigen Datenwert und veraendert das Laufzeitverhalten NICHT - der
 * Zugriff laeuft weiterhin ueber die volle Prototypenkette und liefert bei `"constructor"` den
 * geerbten Wert, genau wie im Original (Eigenheit 1 im Kopfkommentar). Ein `Object.hasOwn` waere
 * hier die naheliegende Absicherung, aber eine Verhaltensaenderung - siehe Bericht zu Task 3b.
 */
const ikonenTabelle = STATUS_ICONS as unknown as Record<string, string>;

/**
 * index.html:4145 - im Original eine Prozedur, die Modul-Globals schreibt; hier eine reine
 * Funktion, die dieselben drei Ableitungen als Objekt liefert (siehe Kopfkommentar).
 */
export function setStatusDefinitions(columns: unknown): StatusDefinitionen {
  const source = ensureCompletedBoardColumn(columns);
  const STATUSES: StatusDefinition[] = source
    .slice()
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    .map((column, index) => {
      const color = /^#[0-9a-f]{6}$/i.test(String(column.color || ""))
        ? (column.color as string)
        : "#667085";
      const icon = ikonenTabelle[column.icon as string] ? (column.icon as string) : "list";
      return {
        ...column,
        label: String(column.label || `Spalte ${index + 1}`).trim(),
        color,
        icon,
        soft: statusSoftColor(color),
        iconSvg: ikonenTabelle[icon],
      };
    });
  const STATUS_MAP: Record<string, StatusDefinition> = Object.fromEntries(
    STATUSES.map((status) => [status.id, status]),
  );
  const STATUS_STAGE: Record<string, number> = Object.fromEntries(
    STATUSES.map((status, index) => [
      status.id,
      Number.isInteger(Number(status.stage))
        ? Math.max(0, Math.min(7, Number(status.stage)))
        : STATUSES.length === 1
          ? 7
          : Math.round((index * 7) / (STATUSES.length - 1)),
    ]),
  );
  // index.html:4156-4159 (COLUMN_FILTERS / COLUMN_FILTER_OPEN) bewusst nicht uebernommen -
  // Oberflaechenzustand, Begruendung im Kopfkommentar.
  return { STATUSES, STATUS_MAP, STATUS_STAGE };
}

/**
 * index.html:4164 - bis Phase 6 modulintern (siehe "Notwendige Abhaengigkeiten" im Kopfkommentar).
 * Inzwischen exportiert, von ZWEI Seiten unabhaengig voneinander gefordert - beim Merge von
 * `feat/09-spa-seiten` in `feat/09-react-spa` stand hier derselbe Export mit zwei Begruendungen;
 * beide sind erhalten, der Export ist genau EINER:
 *
 * - Task 11 (Uebersichtsseite) bildet damit `inTransit` (`index.html:14572`,
 *   `active.filter(order => !isArrivedStatus(order.status))`) - der einzige Weg dorthin, ohne den
 *   Rechenweg in einer Komponente zu wiederholen.
 * - Task 10 (Seite „Zeitstrahl", `components/TimelinePage/`) braucht sie fuer den Rueckfall aus dem
 *   Board-Status (`index.html:13744`).
 *
 * Geaendert wurde beidseitig ausschliesslich das vorangestellte `export` - kein Zeichen am Rumpf.
 */
export function isArrivedStatus(status: unknown, definitionen: StatusDefinitionen): boolean {
  return Number(definitionen.STATUS_STAGE[status as string] ?? 0) >= 7;
}

// Die Statusnormalisierung des LADEPFADS (`migrateOrders`, index.html:5831-5835) steht bewusst
// NICHT hier, sondern in `state/statusNormalisierung.ts`: sie ist kein Rechenweg des Bestands,
// sondern der Ladevorgang selbst - im Original liegt sie in `load()`, nicht bei den
// Status-Hilfsfunktionen. Begruendung im Kopfkommentar dort.

// ================= Dokumente =================

/** index.html:5044 - modulintern. */
function docsForType(o: Order, t: unknown): OrderDocument[] {
  return ((o.pdfs || []) as OrderDocument[]).filter((d) => (d.type || "sonstige") === t);
}

/**
 * index.html:5046. Der Parameter ist im Original nicht deklariert, aber an allen Aufrufstellen
 * angegeben (siehe Eigenheit 3 im Kopfkommentar).
 *
 * ABWEICHUNG vom Original (Asana 1220057765558651, Spec 2026-10-31): das Original liefert immer alle Typen.
 * Der `anzahlungsbeleg` steht nur in der Liste, wenn die Bestellung mehr als eine Rate hat oder bereits einen
 * Anzahlungsbeleg traegt (ein Dokument mit Datei dieses Typs in `pdfs`, eigen oder geerbt - ein vorhandener Beleg wird nie
 * unsichtbar, auch nicht, wenn der Plan wieder auf eine Rate schrumpft). Ein reines Haeckchen in `docsManual`/`docsNa`
 * blendet die Zeile nicht ein: das Backend zaehlt ebenso nur Links, deren Dokument eine Fassung hat (R11).
 * Ohne Bestellung kommen alle Typen. CMR bleibt wie im Original. Gegenstueck zur Sichtbarkeit der Checkliste im
 * Backend (`anzahlungsbelegSichtbarkeit`, `domaene/ratenposition.ts`); ob er Pflicht ist, steht bei `docProgress`.
 */
export function visibleDocTypes(o?: Order): DocType[] {
  const alle = [...DOC_TYPES, CMR_DOC_TYPE];
  if (!o) return alle;
  const mehrereRaten = Array.isArray(o.payments) && o.payments.length > 1;
  return mehrereRaten || docsForType(o, "anzahlungsbeleg").length > 0
    ? alle
    : alle.filter((t) => t.id !== "anzahlungsbeleg");
}

/** Ob ein Belegtyp als vorhanden gilt: hochgeladen ODER manuell/als-entfaellt markiert (index.html:5050-5052). */
function belegVorhanden(o: Order, typeId: unknown): boolean {
  return !!(
    docsForType(o, typeId).length ||
    ((o.docsManual || {}) as Record<string, unknown>)[typeId as string] ||
    ((o.docsNa || {}) as Record<string, unknown>)[typeId as string]
  );
}

/**
 * index.html:5048.
 *
 * ABWEICHUNG vom Original (Asana 1220057765558651, Spec 2026-10-31): Anzahlungs- und Restzahlungs-/Gesamtzahlungsbeleg
 * ersetzen den einen `zahlungsbeleg`; die Split-Altlogik (`zahlungsbeleg_anzahlung`/`zahlungsbeleg_restzahlung`,
 * `SPLIT_PAYMENT_DOC_TYPES`, Zahlungsmodus) entfaellt. Erfuellt ist ein Typ, wenn `belegVorhanden` (wie im Original)
 * oder - fuer die beiden Zahlungsbelege - ein Beleg an der Ist-Zahlung der Rate haengt, die zu diesem Typ gehoert
 * (`istBelegFuer`, nach Ratenposition; Gegenstueck zu `backend/src/domaene/checkliste.ts`). Belege an einer Ist-Zahlung
 * stehen nicht in `pdfs[]`.
 *
 * Der `anzahlungsbeleg` zaehlt in `total` nur, wenn er Pflicht ist (`anzahlungsbelegPflicht`: mehr als eine Rate, Zahlungsziel
 * nie gewechselt) oder schon erfuellt ist; sonst ist er optional und haelt eine Bestellung nicht auf. Alle anderen
 * sichtbaren Typen zaehlen wie im Original, auch die als `optional` gefuehrten.
 *
 * Stand 2026-10-31 (Spec 2026-10-31-board-dokumentstand): `boardDocsComplete`/`isComplete` bauen NICHT auf `docProgress`
 * auf, sondern lesen `dokumentStand` des Servers (siehe dort).
 */
export function docProgress(o: Order): DocProgress {
  let done = 0;
  let total = 0;
  for (const t of visibleDocTypes(o)) {
    const erfuellt =
      belegVorhanden(o, t.id) || (ZAHLUNGSBELEG_TYPEN.has(t.id) && istBelegFuer(o, t.id as Zahlungsbelegtyp));
    if (t.id === "anzahlungsbeleg" && !erfuellt && !anzahlungsbelegPflicht(o)) continue;
    total++;
    if (erfuellt) done++;
  }
  return { done, total };
}

/**
 * index.html:6603.
 *
 * ABWEICHUNG vom Original (Spec 2026-10-31-board-dokumentstand): das Original rechnet im Browser
 * `docProgress(o).done === docProgress(o).total`. Hier gilt das Urteil des Servers - das Lesefeld
 * `dokumentStand` (`lib/dokumentStand.ts#dokumenteVollstaendig`), dieselbe Bewertung wie die
 * Dokumente-Checkliste im Drawer. Sonst stand eine Bestellung mit erfuellter Checkliste („entfaellt",
 * optionale Belege, keine Bestell-Excel) dauerhaft in „Abgeschlossen – Warten auf Dokumente", weil
 * `docProgress` aus `pdfs[]` anders zaehlt. Ohne `dokumentStand` (JTL-Bestellung ohne Hub-Zeile)
 * gilt die Bestellung als nicht vollstaendig.
 */
export function boardDocsComplete(o: Order): boolean {
  return dokumenteVollstaendig(o);
}

// ================= Handlungsbedarf =================

/** index.html:5148 - Handlungsbedarf: 2 = dringend (rot), 1 = bald (gelb), 0 = ok. */
export function actionInfo(o: Order, definitionen: StatusDefinitionen): ActionInfo {
  if (o.archived) return { level: 0, labels: [] };
  const today = todayIso();
  const labels: ActionLabel[] = [];
  let level = 0;
  const nd = nextDue(o);
  if (nd) {
    if (nd < today) {
      labels.push({ text: "Zahlung überfällig", level: 2 });
      level = 2;
    } else {
      const days = daysFromToday(nd);
      // Woertlich: `days <= 7` mit einem moeglichen `null` aus daysFromToday. `null <= 7` ist in
      // JavaScript wahr (null wird zu 0), TypeScript verbietet den Vergleich - deshalb der Cast.
      // Zur Laufzeit unveraendert. Erreichbar ist der Fall hier ohnehin nicht: `nd` ist an dieser
      // Stelle eine nicht-leere Zeichenkette, und nur bei leerem Argument liefert daysFromToday
      // `null`.
      if ((days as number) <= 7) {
        labels.push({ text: `Zahlung fällig ${fmtDate(nd)}`, level: 1 });
        level = Math.max(level, 1);
      }
    }
  }
  if (o.eta && !isArrivedStatus(o.status, definitionen) && (o.eta as string) < today) {
    labels.push({ text: "ETA überfällig", level: 2 });
    level = 2;
  }
  if (isArrivedStatus(o.status, definitionen)) {
    if (!isPaid(o)) {
      labels.push({ text: "Zahlung offen", level: 1 });
      level = Math.max(level, 1);
    }
  }
  return { level, labels };
}

/**
 * index.html:5172 - fertig = bezahlt (alle Raten) + Dokumente vollstaendig.
 *
 * ABWEICHUNG vom Original (Spec 2026-10-31-board-dokumentstand): „Dokumente vollstaendig" ist hier
 * das Urteil des Servers (`dokumentStand`, siehe `boardDocsComplete`), nicht
 * `docProgress(o).done === docProgress(o).total`. Banner „Bestellung vollstaendig" im Drawer und
 * automatische Archivierung urteilen damit wie die Dokumente-Checkliste.
 */
export function isComplete(o: Order): boolean {
  return isPaid(o) && dokumenteVollstaendig(o);
}

/**
 * index.html:5227 - der Handlungsbedarf der ZAHLUNGSANSICHT (`isPaymentManager()`): sie zeigt
 * ausschliesslich Bestellungen mit einem offenen, ausdruecklich angestossenen Zahlungstask, und
 * zwar immer als Stufe 2. Nachportiert in Task 11 (Phase 6) fuer `UebersichtSeite`
 * (`index.html:14577`). Keine Signaturaenderung: die Funktion liest ausser der Bestellung nichts.
 *
 * Der Rueckgabewert ist die einzige Stelle der Portierung, an der ein `ActionLabel` ein `kind`
 * traegt - `actionKind` prueft genau darauf zuerst (`kind === "payment_request"`) und stuft die
 * Zeile damit auch dann als „payment" ein, wenn der Text es nicht hergaebe.
 */
export function paymentRequestActionInfo(order: Order): ActionInfo {
  const task = openPaymentRequestTask(order);
  return task
    ? { level: 2, labels: [{ text: "Zahlung jetzt ausführen", level: 2, kind: "payment_request" }] }
    : { level: 0, labels: [] };
}

/** index.html:5234 */
export function overviewActionInfo(o: Order, definitionen: StatusDefinitionen): ActionInfo {
  if (o.archived) return { level: 0, labels: [] };
  const today = todayIso();
  const labels: ActionLabel[] = [];
  let level = 0;
  const add = (text: string, lvl: number): void => {
    labels.push({ text, level: lvl });
    level = Math.max(level, lvl);
  };
  if (openPaymentRequestTask(o)) add("Zahlung jetzt ausführen", 2);
  const nd = nextDue(o);
  if (nd) {
    if (nd < today) add("Zahlung überfällig", 2);
    else {
      const days = daysFromToday(nd);
      // Siehe Kommentar in actionInfo: reine Typergaenzung.
      if ((days as number) <= 7) add(`Zahlung fällig ${fmtDate(nd)}`, 1);
    }
  }
  if (o.eta && !isArrivedStatus(o.status, definitionen) && (o.eta as string) < today)
    add("ETA überschritten", 2);
  if (!isArrivedStatus(o.status, definitionen) && !o.eta) add("ETA fehlt", 1);
  if (isArrivedStatus(o.status, definitionen)) {
    if (!isPaid(o)) add("Zahlung offen nach Anlieferung", 2);
  }
  if (!o.supplier) add("Lieferant fehlt", 1);
  if (!o.orderNo) add("Bestellnr. fehlt", 1);
  // Die Casts sind reine Typarbeit (seit Task 1 sind `name`/`sku` in `OrderItem` `unknown`, siehe
  // `lib/types.ts`): `it.name || ''` liefert bei einem wahrhaftigen Nicht-String weiterhin diesen
  // Wert selbst, und `.trim()` wirft dort - genau wie im Original.
  if (
    !((o.items || []) as OrderItem[]).some(
      (it) => ((it.name || "") as string).trim() || ((it.sku || "") as string).trim(),
    )
  )
    add("Artikel fehlen", 1);
  if (!isPaid(o) && !((o.payments || []) as OrderPayment[]).length && !o.paymentTerms)
    add("Zahlungsplan fehlt", 1);
  return { level, labels };
}

// ================= Board-Gruppierung =================

/** index.html:6608 - modulintern; liest im Original das globale `DB.orders`. */
function isSplitCollectionOrder(o: Order, alleBestellungen: readonly Order[]): boolean {
  return !!o.splitFrom || alleBestellungen.some((candidate) => candidate.splitFrom === o.id);
}

/**
 * index.html:6616 - nicht Teil der Portierungsliste von Task 3b (das diesen Kopfkommentar
 * ansonsten traegt), sondern additiv mit Task 6 hinzugekommen: `src/lib/fx.ts` braucht sie fuer
 * `fxWeekBuckets` (ueber `paymentCalendarRows`, siehe unten). Rein additiv, wie `boardGroupId` und
 * `orderListDue`, die bereits hier lagen - Praezedenzfall `normSearch` in Task 5a. Im Original
 * liest sie das globale `DB.orders`; hier kommt die Liste als Argument `orders`.
 *
 * Der Split-Status-Ausschluss (`isSplitStatusOrder`, index.html:6612) ist mit der Statusbereinigung
 * entfallen: eine Sammellieferungs-Quelle bekommt seither `archived=true` (siehe
 * `backend/src/jtl/schreiben/sammellieferung.ts`) und wird darueber ausgeschlossen - der
 * Archiviert-Ausschluss uebernimmt die Wirkung. `definitionen` bleibt Teil der Signatur
 * (unveraendert fuer alle Aufrufer), wird hier aber nicht mehr gelesen.
 */
export function activeReportingOrders(
  orders: readonly Order[],
  _definitionen: StatusDefinitionen,
): Order[] {
  return orders.filter((order) => !order.archived);
}

/**
 * index.html:6620. Im Original bezieht `isSplitCollectionOrder` die uebrigen Bestellungen aus dem
 * globalen `DB.orders`; hier werden sie als `alleBestellungen` hereingereicht.
 */
export function boardGroupId(
  o: Order,
  definitionen: StatusDefinitionen,
  alleBestellungen: readonly Order[],
): string {
  if (BOARD_GROUPS.some((group) => group.id === o.boardGroupOverride)) return o.boardGroupOverride as string;
  // Seit Task 1218053584680825 (2026-09-01) gibt es die Board-Gruppen "split"/"completed" nicht
  // mehr (siehe `board.ts`, `BOARD_GROUPS`). Beide Faelle werden bewusst explizit auf "open"
  // gelenkt statt einfach entfernt zu werden - sonst wuerde z.B. eine Split-Bestellung mit offener
  // Zahlung in den naechsten Zeilen "waiting-payment" statt "open" treffen, was dem Auftrag
  // widerspricht. Die `isComplete`-Zeile bleibt als Absicherung fuer den kurzen Moment zwischen
  // "vollstaendig geworden" und "automatisch archiviert" (siehe Archivierungs-Sweep in `App.tsx`) -
  // archivierte Bestellungen werden vor dem Board-Rendering ohnehin ausgefiltert.
  if (isSplitCollectionOrder(o, alleBestellungen)) return "open";
  if (isComplete(o)) return "open";
  if (isArrivedStatus(o.status, definitionen) && !isPaid(o)) return "waiting-payment";
  if (isArrivedStatus(o.status, definitionen) && !boardDocsComplete(o)) return "waiting-docs";
  return "open";
}

// ================= Alter und Faelligkeit =================

/** index.html:7198 */
export function orderAgeDays(o: Order): number | null {
  const source = (o.orderDate || String(o.createdAt || "").slice(0, 10)) as string;
  if (!source) return null;
  // Im Original eine Date-Subtraktion (`new Date(...) - new Date(...)`); TypeScript erlaubt die
  // implizite Zahl-Koerzion zweier Date-Objekte nicht. `.getTime()` liefert denselben Zahlenwert
  // wie die von `valueOf()` erzwungene Original-Subtraktion - reine Typergaenzung.
  const ms =
    new Date(todayIso() + "T00:00:00Z").getTime() -
    new Date(source.slice(0, 10) + "T00:00:00Z").getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 864e5) : null;
}

/** index.html:7205 */
export function isRecentOrder(o: Order): boolean {
  const age = orderAgeDays(o);
  return age !== null && age >= 0 && age < 10;
}

/** index.html:7211 */
export function orderListDue(o: Order): string {
  if (Object.prototype.hasOwnProperty.call(o || {}, "listDueDate")) return (o.listDueDate as string) || "";
  const taskDue = ((o.tasks || []) as OrderTask[])
    .filter((task) => !task.done && task.due)
    .map((task) => task.due)
    .sort()[0];
  // Der Cast ist reine Typarbeit (seit Task 1 ist `due` in `OrderTask` `unknown`, siehe
  // `lib/types.ts`): das Laufzeitverhalten ist unveraendert, `taskDue` ist entweder ein
  // gespeicherter Wert oder `undefined`.
  return (taskDue as string) || nextDue(o) || "";
}

// ================= Zahlungskalender (additiv, Task 6) =================
//
// index.html:13354 - nicht Teil der Portierungsliste von Task 3b, sondern additiv mit Task 6
// hinzugekommen: `src/lib/fx.ts` braucht sie fuer `fxWeekBuckets`. In `lib/payments.ts` ergaebe sie
// einen Importzyklus (sie braucht `orderListDue` von hier, und dieses Modul importiert bereits
// `lib/payments.ts`) - siehe Bericht zu Task 6.

/** Eine Zeile des Zahlungskalenders (`paymentCalendarRows`). */
export interface PaymentCalendarRow {
  order: Order;
  payment: OrderPayment | null;
  label: string;
  amount: number;
  usd: number;
  due: string;
  paid: boolean;
}

/**
 * index.html:13354. Im Original liest sie das globale `DB.orders` (ueber `activeReportingOrders`);
 * hier kommt die Liste als Argument `orders`. `suppliers` wird gebraucht, weil `orderCurrency`
 * (`src/lib/suppliers.ts`) die Lieferantenliste als Argument nimmt (Task 4).
 */
export function paymentCalendarRows(
  orders: readonly Order[],
  definitionen: StatusDefinitionen,
  suppliers: readonly Supplier[] | null | undefined,
): PaymentCalendarRow[] {
  const rows: PaymentCalendarRow[] = [];
  for (const order of activeReportingOrders(orders, definitionen)) {
    const payments = (order.payments || []) as OrderPayment[];
    if (payments.length) {
      for (const payment of payments) {
        const amount = payAmount(order, payment);
        if (amount <= 0) continue;
        rows.push({
          order,
          payment,
          label: (payment.label as string) || "Zahlung",
          amount,
          usd: orderCurrency(order, suppliers) === "USD" ? amount : 0,
          // Kein Rueckfall auf die Faelligkeit der Bestellung (Auftrag 2026-10-01, R5): eine Rate ohne
          // Datum bleibt eine Rate ohne Datum.
          due: (payment.due as string) || "",
          paid: paymentStatusOf(payment) === "paid" || isPaid(order),
        });
      }
    } else if (orderTotal(order) > 0) {
      rows.push({
        order,
        payment: null,
        label: "Gesamtzahlung",
        amount: orderTotal(order),
        usd: orderCurrency(order, suppliers) === "USD" ? orderTotal(order) : 0,
        due: "",
        paid: isPaid(order),
      });
    }
  }
  return rows;
}

// ================= Zeitstrahl =================

/**
 * index.html:13693.
 *
 * NACHBESSERUNG (W3, Pruefung d4dd340): `stufeFuerStatus` stand hier zunaechst selbst (als
 * `export { stufeFuerStatus }`, um `scripts/gen-herkunft.ts`s Herkunftsmarken-Pflicht zu umgehen) -
 * das machte das Symbol fuer den Waechter unsichtbar statt es korrekt auszunehmen (Einzelheiten im
 * Kopfkommentar von `lib/zeitstrahlStufen.ts`, wohin die Funktion jetzt gehoert, nach dem
 * Praezedenzfall `lib/lager.ts`/`lib/orderListEditor.ts`). Hier bleibt nur noch der Aufruf.
 */
export function orderStageIndex(o: Order, definitionen: StatusDefinitionen): number {
  return stufeFuerStatus(o.status, definitionen);
}

// ================= Labels des Handlungsbedarfs =================

/** index.html:14263 */
export function actionKind(labels: readonly ActionLabel[]): string {
  if (labels.some((label) => label.kind === "payment_request")) return "payment";
  const text = labels
    .map((l) => l.text)
    .join(" ")
    .toLowerCase();
  if (text.includes("zahlung")) return "payment";
  if (text.includes("eta")) return "eta";
  if (text.includes("lieferant") || text.includes("bestellnr") || text.includes("artikel"))
    return "master";
  return "other";
}

/** index.html:14272 */
export function actionOwner(labels: readonly ActionLabel[]): string {
  const kind = actionKind(labels);
  if (kind === "payment") return "Zahlung";
  if (kind === "eta") return "Import";
  return "Einkauf";
}

/** index.html:14279 */
export function actionSince(order: Order, labels: readonly ActionLabel[]): string {
  const text = labels
    .map((l) => l.text)
    .join(" ")
    .toLowerCase();
  if (
    labels.some((label) => label.kind === "payment_request") ||
    text.includes("zahlung jetzt ausführen")
  ) {
    return ((order.paymentInitiatedAt || openPaymentRequestTask(order)?.updatedAt) as string) || "";
  }
  const due = nextDue(order);
  if (text.includes("zahlung") && due) return due;
  if (text.includes("eta") && order.eta) return order.eta as string;
  return ((order.orderDate || order.createdAt) as string) || "";
}

/**
 * index.html:14290.
 *
 * ABWEICHUNG vom Original (Auftrag "'Offene Rate pruefen' kann eine Schlusszahlung faelschlich als
 * Teilzahlung darstellen"): der /zahlung offen/i-Zweig trifft sowohl auf das Label "Zahlung offen"
 * als auch auf "Zahlung offen nach Anlieferung" (`overviewActionInfo`) - beide koennen ebenso gut
 * eine vollstaendige Schlusszahlung wie eine Ratenzahlung meinen. Das Original nennt hier immer
 * "Rate", was fuer eine Schlusszahlung fachlich falsch ist; die Portierung spricht deshalb
 * neutral von "Zahlung". Nur dieser eine Rueckgabetext weicht ab - die Label-Erzeugung und alle
 * anderen Zweige bleiben unveraendert woertlich.
 */
export function nextActionText(labels: readonly ActionLabel[]): string {
  const first = labels[0]?.text || "";
  if (/zahlung jetzt ausführen/i.test(first)) return "Zahlung ausführen";
  if (/zahlung überfällig/i.test(first)) return "Zahlung klären";
  if (/zahlung fällig/i.test(first)) return "Zahlung einplanen";
  if (/zahlung offen/i.test(first)) return "Offene Zahlung prüfen";
  if (/eta über/i.test(first)) return "Liefertermin eskalieren";
  if (/eta fehlt/i.test(first)) return "ETA beim Lieferanten anfragen";
  if (/lieferant/i.test(first)) return "Lieferant ergänzen";
  if (/artikel/i.test(first)) return "Positionen vervollständigen";
  if (/zahlungsplan/i.test(first)) return "Zahlungsplan pflegen";
  return "Bestellung öffnen";
}
